import React, { useState, useEffect, useCallback } from "react";
import { useAppStore } from "@/store/appContext";
import { config } from "@/utils/config";
import { ShopifyService } from "@/utils/shopifyService";
import { Spinner } from "@/components/atomic";
import { qrPollingService } from "@/utils/qrPollingService";
import ThankYouModal from "./ThankYouModal";

const ConfirmationModal: React.FC = () => {
  const {
    localCart,
    products,
    isConfirmationSheetOpen,
    closeConfirmationSheet,
    setOrderConfirmed,
    confirmDonation,
    setCart,
    setLoading,
    setError,
    clearLocalCart,
    selectedProperty,
    selectedKiosk,
    sessionId,
    isQRScanned,
    setSessionId,
    setQRScanned,
    showConfetti,
    setShowConfetti,
  } = useAppStore();

  const [timeLeft, setTimeLeft] = useState(120);
  const [isExpired, setIsExpired] = useState(false);
  const [isItemsListExpanded, setIsItemsListExpanded] = useState(false);
  const [isCreatingCart, setIsCreatingCart] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [cartCreationFailed, setCartCreationFailed] = useState(false);

  const getSellingPlanName = (sellingPlanId?: string) => {
    if (!sellingPlanId || sellingPlanId === "") return "One-time";

    // Find the selling plan name from products
    for (const product of products) {
      if (product.sellingPlanGroups?.edges) {
        for (const group of product.sellingPlanGroups.edges) {
          for (const plan of group.node.sellingPlans.edges) {
            if (plan.node.id === sellingPlanId) {
              return plan.node.name;
            }
          }
        }
      }
    }
    return "Recurring";
  };

  const generateCartPermalink = () => {
    if (checkoutUrl) {
      return checkoutUrl;
    }
    const cartItems = localCart.items
      .map((item) => {
        const numericVariantId = item.variantId.replace(
          "gid://shopify/ProductVariant/",
          ""
        );
        return `${numericVariantId}:${item.quantity}`;
      })
      .join(",");

    const baseUrl = `https://${config.shopify.storeUrl}/cart/${cartItems}`;
    const params = new URLSearchParams({
      access_token: config.shopify.storefrontToken,
    });
    return `${baseUrl}?${params.toString()}`;
  };

  const createShopifyCart = useCallback(async () => {
    try {
      setIsCreatingCart(true);
      setCartCreationFailed(false);

      const shopifyService = ShopifyService.getInstance();
      const cartItems = localCart.items.map((item) => ({
        variantId: item.variantId,
        quantity: item.quantity,
        sellingPlanId: item.sellingPlanId,
      }));

      const { cart: shopifyCart, sessionId: newSessionId } =
        await shopifyService.createCart(
          cartItems,
          selectedProperty,
          selectedKiosk
        );
      const convertedCart =
        shopifyService.convertShopifyCartToCart(shopifyCart);

      // Update the main cart with Shopify cart data
      setCart(convertedCart, shopifyCart.id);

      // Set session ID for QR polling
      setSessionId(newSessionId);

      // Set checkout URL for QR code
      setCheckoutUrl(convertedCart.checkoutUrl ?? null);
    } catch (error) {
      console.error("Error creating Shopify cart:", error);
      setCartCreationFailed(true);
      setError("Failed to process donation. Please try again.");
    } finally {
      setIsCreatingCart(false);
    }
  }, [
    localCart.items,
    setCart,
    setError,
    generateCartPermalink,
    selectedProperty,
    selectedKiosk,
  ]);

  // Create cart when modal opens
  useEffect(() => {
    if (
      isConfirmationSheetOpen &&
      !checkoutUrl &&
      !isCreatingCart &&
      !cartCreationFailed
    ) {
      createShopifyCart();
    }
  }, [
    isConfirmationSheetOpen,
    checkoutUrl,
    isCreatingCart,
    cartCreationFailed,
  ]);

  // Reset failed state when modal closes
  useEffect(() => {
    if (!isConfirmationSheetOpen) {
      setCartCreationFailed(false);
      setCheckoutUrl(null);
      setQRScanned(false);
      setSessionId(null);
      setShowConfetti(false);
      // Stop polling when modal closes
      if (qrPollingService.getIsPolling()) {
        qrPollingService.stopPolling();
      }
    }
  }, [isConfirmationSheetOpen, setQRScanned, setSessionId, setShowConfetti]);

  // Timer logic - only start after checkoutUrl is available
  useEffect(() => {
    if (!isConfirmationSheetOpen || !checkoutUrl) {
      setTimeLeft(120);
      setIsExpired(false);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsExpired(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isConfirmationSheetOpen, checkoutUrl]);

  // Auto-confirm when timer expires
  useEffect(() => {
    if (isExpired) {
      setTimeout(() => {
        handleConfirmDonation();
      }, 1000);
    }
  }, [isExpired]);

  const handleConfirmDonation = useCallback(async () => {
    try {
      setLoading(true);

      // Clear local cart
      clearLocalCart();

      // Confirm donation
      confirmDonation();
      setOrderConfirmed(true);
      closeConfirmationSheet();
    } catch (error) {
      console.error("Error confirming donation:", error);
      setError("Failed to confirm donation. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [
    clearLocalCart,
    confirmDonation,
    setOrderConfirmed,
    closeConfirmationSheet,
    setLoading,
    setError,
  ]);

  // Start QR polling when session ID is available
  useEffect(() => {
    if (sessionId && isConfirmationSheetOpen && !isQRScanned) {
      const startPolling = async () => {
        try {
          await qrPollingService.startPolling(
            sessionId,
            () => {
              setQRScanned(true);
              setShowConfetti(true);
            },
            (error) => {
              console.error("❌ QR polling error:", error);
            }
          );
        } catch (error) {
          console.error("❌ Failed to start QR polling:", error);
        }
      };

      startPolling();
    }

    // Cleanup polling when component unmounts or session changes
    return () => {
      if (qrPollingService.getIsPolling()) {
        qrPollingService.stopPolling();
      }
    };
  }, [
    sessionId,
    isConfirmationSheetOpen,
    isQRScanned,
    handleConfirmDonation,
    setQRScanned,
  ]);

  const handleClose = () => {
    closeConfirmationSheet();
  };

  const isOnlineKiosk = selectedKiosk?.id === "online";

  if (!isConfirmationSheetOpen) return null;

  return (
    <>
      <ThankYouModal />
      {!showConfetti && (
        <div className="confirmation-sheet-overlay">
          <div
            className="confirmation-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="confirmation-header">
              <h2>Complete Your Donation</h2>
              <button
                className="close-btn"
                onClick={handleClose}
                disabled={isExpired}
              >
                ×
              </button>
            </div>

            <div className="confirmation-content">
              {/* QR Code or Checkout Link Section - Hide when scanned */}
              {!isQRScanned && (
                <div className="qr-code-section">
                  {isCreatingCart ? (
                    <div className="qr-code-container">
                      <Spinner size="large" />
                      <p>Creating your donation...</p>
                    </div>
                  ) : cartCreationFailed ? (
                    <div className="qr-code-container">
                      <div className="error-message">
                        <p>❌ Failed to create donation</p>
                        <p>Please try again or contact support</p>
                      </div>
                    </div>
                  ) : checkoutUrl ? (
                    <div className="qr-code-container">
                      {isOnlineKiosk ? (
                        <>
                          <a
                            href={checkoutUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="checkout-link-plain"
                          >
                            Click To Pay
                          </a>
                        </>
                      ) : (
                        <>
                          <img
                            src={`${config.qrCode.apiUrl}${encodeURIComponent(
                              checkoutUrl
                            )}`}
                            alt="QR Code for Payment"
                            className="qr-code-image"
                          />
                          <p>
                            {isQRScanned
                              ? "✅ Payment detected! Processing..."
                              : "Scan QR Code to Complete Payment"}
                          </p>
                        </>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              {/* Countdown Timer - Hide when scanned */}
              {checkoutUrl && !isQRScanned && (
                <div className="countdown-section">
                  <div className="countdown-timer">
                    {isExpired
                      ? "Processing..."
                      : `${Math.floor(timeLeft / 60)}m ${timeLeft % 60}s`}
                  </div>
                  <div className="countdown-label">
                    {isOnlineKiosk
                      ? "Complete your payment before the timer runs out"
                      : "Scan to free up the kiosk for next devotee"}
                  </div>
                </div>
              )}

              {/* Instamandi Bonus Banner */}
              <div className="instamandi-bonus-banner">
                <div className="bonus-icon">🎁</div>
                <div className="bonus-text">
                  <div className="bonus-title">
                    Instamandi will add 20% of your donation
                  </div>
                  <div className="bonus-subtitle">
                    Extra support for your cause
                  </div>
                </div>
              </div>

              {/* Donation Summary */}
              <div className="donation-summary">
                <div className="summary-row">
                  <span>Tax:</span>
                  <span>Calculated at checkout</span>
                </div>

                <div className="summary-row">
                  <span>Shipping:</span>
                  <span>Calculated at checkout</span>
                </div>

                <div className="summary-row total">
                  <span>Donation Total:</span>
                  <span>${localCart.totalPrice.toFixed(2)}</span>
                </div>

                {/* Items List */}
                <div className="items-list">
                  <div
                    className="items-list-header"
                    onClick={() => setIsItemsListExpanded(!isItemsListExpanded)}
                  >
                    <h4>Items ({localCart.totalQuantity})</h4>
                    <span
                      className={`toggle-icon ${
                        isItemsListExpanded ? "expanded" : ""
                      }`}
                    >
                      ⌄
                    </span>
                  </div>

                  {isItemsListExpanded && (
                    <div className="items-list-content">
                      {localCart.items.map((item) => (
                        <div
                          key={`${item.productId}-${item.variantId}`}
                          className="item-row"
                        >
                          <div className="item-image">
                            {item.image ? (
                              <img src={item.image} alt={item.title} />
                            ) : (
                              <div className="item-placeholder">📦</div>
                            )}
                          </div>
                          <div className="item-details">
                            <div className="item-title">{item.title}</div>
                            {item.variantTitle !== "Default Title" && (
                              <div className="item-variant">
                                {item.variantTitle}
                              </div>
                            )}
                            <div className="item-selling-plan">
                              {getSellingPlanName(item.sellingPlanId)}
                            </div>
                            <div className="item-quantity">
                              Qty: {item.quantity}
                            </div>
                            <div className="item-price">
                              ${(item.price * item.quantity).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Totals Section */}
                <div className="totals-section">
                  <div className="summary-row">
                    <span>Total Items:</span>
                    <span>{localCart.totalQuantity}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConfirmationModal;
